import { z } from "zod";
import { client } from "./client";
import { validateResponse } from "@/api/utils";
import type { FilterOption } from "@/components/designSystem/FilterSelect";

/** 충전소 옵션 1건. value 는 uuid — 충전소를 가리키는 모든 필터가 uuid 축이다 */
export interface StationOption extends FilterOption {
  /** 소속 운영사. 없을 수 있고, 그때는 운영사 연쇄 선택에서 제외된다 */
  cpoId?: number;
}

interface StationNameRow {
  uuid?: string | null;
  name?: string | null;
  cpo?: { id?: number | null } | null;
}

/**
 * 충전소 옵션 (소속 운영사 포함). name-list 는 전량을 이름 오름차순으로 내려준다.
 * 서버 필터(`?cpo_ids=`) 대신 전량을 캐시하고 클라이언트에서 걸러 재조회를 없앤다.
 */
export const getStationOptions = async (): Promise<StationOption[]> => {
  try {
    const response = await client.get("/station/name-list");
    const rows: StationNameRow[] = response.data.data ?? [];

    return rows.flatMap((row) =>
      row.uuid
        ? [
            {
              value: row.uuid,
              label: row.name ?? row.uuid,
              cpoId: row.cpo?.id ?? undefined,
            },
          ]
        : [],
    );
  } catch (e) {
    console.warn("Failed to fetch station options:", e);
    return [];
  }
};

const usableStationSchema = z.array(
  z.object({
    uuid: z.string(),
    name: z.string(),
    cpo: z.object({ id: z.number() }).nullish(),
  }),
);

/** 고객사 이용 가능 충전소(계약 이용 충전소 합집합). CPO·MASTER 는 403 이라 name-list 를 쓴다 */
export const getUsableStationOptions = async (): Promise<StationOption[]> => {
  const endpoint = "/charging-history/station-options";
  const response = await client.get(endpoint);
  return validateResponse(
    usableStationSchema,
    response.data.data,
    "GET",
    endpoint,
  ).map((row) => ({
    value: row.uuid,
    label: row.name,
    cpoId: row.cpo?.id ?? undefined,
  }));
};

/** 운영사 선택으로 충전소 옵션을 좁힌다. 미선택이면 전체, 운영사 없는 충전소는 제외 */
export const filterStationOptionsByCpo = (
  options: StationOption[],
  cpoIds?: number[] | null,
): StationOption[] =>
  cpoIds?.length
    ? options.filter(
        (option) => option.cpoId != null && cpoIds.includes(option.cpoId),
      )
    : options;
